import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { useToast } from '@/hooks/use-toast';
import { formatSupabaseError } from '@/lib/queryHelpers';

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  source: string;
  status: string;
  notes: string;
  estimated_value: number;
  created_at: string;
  updated_at: string;
  user_id: string;
  tenant_id: string;
}

export interface CreateLeadData {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  source?: string;
  status?: string;
  notes?: string;
  estimated_value?: number;
}

export interface UpdateLeadData extends Partial<CreateLeadData> {}

/**
 * Hook for managing leads with React Query
 * Replaces direct Supabase queries in Leads.tsx
 */
export function useLeads() {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch leads
  const {
    data: leadsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['leads', tenantId],
    queryFn: async () => {
      if (!user || !tenantId) return [];

      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Lead[];
    },
    enabled: !!user && !!tenantId,
  });

  // Ensure we always return an array, even if cache contains null
  const leads = leadsData ?? [];

  // Create lead
  const createLead = useMutation({
    mutationFn: async (data: CreateLeadData) => {
      if (!user || !tenantId) throw new Error('User not authenticated');

      const { data: newLead, error } = await supabase
        .from('leads')
        .insert({
          ...data,
          user_id: user.id,
          tenant_id: tenantId,
        })
        .select()
        .single();

      if (error) throw error;
      return newLead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', tenantId] });
      toast({
        title: 'Success',
        description: 'Lead created successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: formatSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // Update lead
  const updateLead = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateLeadData }) => {
      if (!user || !tenantId) throw new Error('User not authenticated');

      const { data: updatedLead, error } = await supabase
        .from('leads')
        .update(data)
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;
      return updatedLead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', tenantId] });
      toast({
        title: 'Success',
        description: 'Lead updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: formatSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // Delete lead
  const deleteLead = useMutation({
    mutationFn: async (id: string) => {
      if (!user || !tenantId) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', tenantId] });
      toast({
        title: 'Success',
        description: 'Lead deleted successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: formatSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  return {
    leads,
    isLoading,
    error,
    refetch,
    createLead: createLead.mutateAsync,
    updateLead: updateLead.mutateAsync,
    deleteLead: deleteLead.mutateAsync,
    isCreating: createLead.isPending,
    isUpdating: updateLead.isPending,
    isDeleting: deleteLead.isPending,
  };
}
